import React, { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useScopeControlCounts } from '../../../hooks/analytics/useScopeReports.ts';
import { WidgetCard } from '../controller/shared.tsx';
import { BAR_RADIUS, CHART_AXIS_LINE, CHART_AXIS_TICK, CHART_GRID_STROKE, CHART_TOOLTIP_STYLE, chartColorFor } from '../theme.ts';

const LEVEL_ORDER = ['high', 'medium', 'low', 'unrated'];

/** `scope_controls_by_risk` — controls in scope bucketed by (normalized) risk level. */
export const ScopeControlsByRiskChart: React.FC = () => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useScopeControlCounts();

  const chartData = useMemo(() => {
    const byLevel = new Map((data?.by_risk_level ?? []).map((r) => [r.level.toLowerCase(), r.count]));
    return LEVEL_ORDER.filter((level) => byLevel.has(level)).map((level) => ({
      level: level.charAt(0).toUpperCase() + level.slice(1),
      count: byLevel.get(level) ?? 0,
      rawLevel: level,
    }));
  }, [data]);

  return (
    <WidgetCard
      title="Controls by Risk Level"
      description="In-scope controls grouped by mapped risk level"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load controls by risk level.'}
      isEmpty={!isLoading && !isError && chartData.length === 0}
      emptyMessage="No risk-level data available yet."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
    >
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />
            <XAxis dataKey="level" tick={CHART_AXIS_TICK} axisLine={CHART_AXIS_LINE} tickLine={false} />
            <YAxis tick={CHART_AXIS_TICK} axisLine={CHART_AXIS_LINE} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={CHART_TOOLTIP_STYLE} cursor={{ fill: 'rgba(148, 163, 184, 0.08)' }} />
            <Bar dataKey="count" name="Controls" radius={BAR_RADIUS} maxBarSize={56}>
              {chartData.map((entry) => (
                <Cell key={entry.rawLevel} fill={chartColorFor(entry.rawLevel)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </WidgetCard>
  );
};

export default ScopeControlsByRiskChart;
